import type {ContentReviewDto} from '@devmil/contracts';
import {Fragment, useId} from 'react';

import {formatQueueDay, formatTimeOfDay} from '../../lib/format.js';
import {ContentQueueItem} from './ContentQueueItem.js';

interface Slot {
  contentItemId: string;
  localDate: string;
  scheduledAt: string;
  seriesName: string;
  pieces: ContentReviewDto[];
}

/**
 * Um bloco por ITEM, na ordem em que a API entregou (dia, horário, espécie).
 *
 * A agregação preserva a ordem de chegada em vez de reordenar: a consulta já
 * ordena por `local_date, scheduled_time, kind`, e reordenar aqui seria uma
 * segunda opinião sobre a mesma pergunta.
 */
const agruparPorSlot = (pieces: readonly ContentReviewDto[]): Slot[] => {
  const slots: Slot[] = [];
  const porItem = new Map<string, Slot>();
  for (const piece of pieces) {
    const aberto = porItem.get(piece.contentItemId);
    if (aberto) {
      aberto.pieces.push(piece);
      continue;
    }
    const slot: Slot = {
      contentItemId: piece.contentItemId,
      localDate: piece.localDate,
      scheduledAt: piece.scheduledAt,
      seriesName: piece.series.name,
      pieces: [piece],
    };
    porItem.set(piece.contentItemId, slot);
    slots.push(slot);
  }
  return slots;
};

/**
 * O resumo do cabeçalho conta a LISTA, e não o dia.
 *
 * Antes ele mostrava `counters.generated`, que fala das peças do dia inteiro, e
 * o resultado era um cabeçalho dizendo "6 ITENS" sobre uma lista de quatro. O
 * número que fica ao lado de uma lista é lido como o tamanho dela, e quem
 * conferisse contando na tela encontraria outro. As peças do dia continuam
 * anunciadas no bloco de resumo, que é onde a pergunta é essa.
 */
const resumoDaLista = (pieces: readonly ContentReviewDto[]): string => {
  const aguardando = pieces.filter(
    (piece) => piece.status === 'awaiting_approval',
  ).length;
  const total = pieces.length === 1 ? '1 peça' : `${pieces.length} peças`;
  return `${total} · ${aguardando} aguardando`;
};

export const ContentQueue = ({
  pieces,
  operationDate,
  selectedId,
  onSelect,
}: {
  pieces: readonly ContentReviewDto[];
  /**
   * O dia da operação, vindo do mesmo snapshot. É ele que decide qual bloco
   * ganha o rótulo "Hoje", e por isso não é lido do relógio da máquina.
   */
  operationDate: string;
  selectedId: string;
  onSelect: (contentVersionId: string) => void;
}) => {
  const headingId = useId();
  const slots = agruparPorSlot(pieces);
  // O separador de dia só aparece quando há mais de um dia na fila. Num dia
  // comum ele seria uma linha repetindo o que o título da página já diz.
  const separarDias = new Set(pieces.map((piece) => piece.localDate)).size > 1;

  return (
    <section className="dm-panel" aria-labelledby={headingId}>
      <div className="dm-panel-head">
        <h2 id={headingId}>Conteúdo do dia</h2>
        <span className="dm-illustrative">{resumoDaLista(pieces)}</span>
      </div>
      <div className="dm-queue">
        {slots.map((slot, index) => {
          const abreDia =
            separarDias &&
            (index === 0 || slots[index - 1]!.localDate !== slot.localDate);

          return (
            <Fragment key={slot.contentItemId}>
              {abreDia ? (
                <p className="dm-daybreak">
                  {formatQueueDay(slot.localDate, operationDate)}
                </p>
              ) : null}
              <div className="dm-slot">
                {/*
                  O espaço entre o horário e a série é ESCRITO, e não só o
                  vão do flex: quem lê a tela por leitor de tela recebe
                  `textContent`, e sem ele o cabeçalho vira "09:00Impacto".
                */}
                <p className="dm-slot-head">
                  <span className="dm-slot-time">
                    {formatTimeOfDay(slot.scheduledAt)}
                  </span>{' '}
                  {slot.seriesName}
                </p>
                {slot.pieces.map((piece) => (
                  <ContentQueueItem
                    key={piece.contentVersionId}
                    piece={piece}
                    selected={piece.contentVersionId === selectedId}
                    onSelect={onSelect}
                  />
                ))}
              </div>
            </Fragment>
          );
        })}
      </div>
    </section>
  );
};
